import React, { useState, useEffect, useMemo } from 'react';
import {
  ShieldCheckIcon,
  ZapIcon,
  ActivityIcon,
  RefreshIcon,
} from '../icons.js';
import {
  NodeCapability,
  ModelRuntime,
  ModelDefinition,
  ModelAssignment,
  RoutingDecision,
} from '../../mesh/orchestration/contracts/model-orchestration-contracts.js';
import { BrowserOrchestrationStore } from '../../mesh/orchestration/storage/browser-orchestration-store.js';
import { NodeCapabilityRegistry } from '../../mesh/orchestration/nodes/node-capability-registry.js';
import { ModelDefinitionRegistry } from '../../mesh/orchestration/catalog/model-definition-registry.js';
import { NeedleRuntimeService } from '../../mesh/orchestration/runtime/needle-runtime-service.js';
import { LlmRuntimeService } from '../../mesh/orchestration/runtime/llm-runtime-service.js';
import { OrchestrationRouter } from '../../mesh/orchestration/routing/orchestration-router.js';
import { ModelAssignmentStore } from '../../mesh/orchestration/assignments/model-assignment-store.js';
import { AgentChannelRegistry } from '../../agents/channels/agent-channel-registry.js';
import { RagCollectionRegistry } from '../../agents/grounding/rag-collection-registry.js';
import { AgentChannelDefinition, RagCollectionDefinition } from '../../agents/channels/agent-channel-contracts.js';
import { AgentQualityLearningDashboard } from '../learning/AgentQualityLearningDashboard.js';

export interface ModelOrchestrationDashboardProps {
  readonly store?: BrowserOrchestrationStore;
  readonly onNotice?: (msg: string) => void;
}

export const ModelOrchestrationDashboard: React.FC<ModelOrchestrationDashboardProps> = ({
  store: propStore,
  onNotice,
}) => {
  const store = useMemo(() => propStore || new BrowserOrchestrationStore(), [propStore]);
  const nodeRegistry = useMemo(() => new NodeCapabilityRegistry(store), [store]);
  const modelRegistry = useMemo(() => new ModelDefinitionRegistry(store), [store]);
  const assignmentStore = useMemo(() => new ModelAssignmentStore(store), [store]);
  const needleService = useMemo(() => new NeedleRuntimeService(store, nodeRegistry, modelRegistry), [store, nodeRegistry, modelRegistry]);
  const llmService = useMemo(() => new LlmRuntimeService(store, nodeRegistry, modelRegistry), [store, nodeRegistry, modelRegistry]);
  const router = useMemo(() => new OrchestrationRouter(store, nodeRegistry, assignmentStore), [store, nodeRegistry, assignmentStore]);
  const channelRegistry = useMemo(() => new AgentChannelRegistry(store), [store]);
  const ragRegistry = useMemo(() => new RagCollectionRegistry(), []);

  const [activeTab, setActiveTab] = useState<'fleet' | 'models' | 'channels' | 'knowledge' | 'learning' | 'simulation'>('fleet');
  const [nodes, setNodes] = useState<readonly NodeCapability[]>([]);
  const [runtimes, setRuntimes] = useState<readonly ModelRuntime[]>([]);
  const [models, setModels] = useState<readonly ModelDefinition[]>([]);
  const [channels, setChannels] = useState<readonly AgentChannelDefinition[]>([]);
  const [collections, setCollections] = useState<readonly RagCollectionDefinition[]>([]);
  const [selectedNode, setSelectedNode] = useState<NodeCapability | null>(null);

  // Simulation form state
  const [simKind, setSimKind] = useState<'needle2' | 'llm'>('needle2');
  const [simScope, setSimScope] = useState<string>('channel');
  const [simScopeId, setSimScopeId] = useState<string>('shopping-cart-assistant');
  const [simResult, setSimResult] = useState<RoutingDecision | null>(null);

  // Force kill modal state
  const [forceKillTarget, setForceKillTarget] = useState<ModelRuntime | null>(null);

  const refreshState = async () => {
    await modelRegistry.init();
    const loadedNodes = await nodeRegistry.getAllNodes();
    if (loadedNodes.length === 0) {
      // Seed local master node
      await nodeRegistry.registerNode({
        nodeId: 'master-node',
        nodeName: 'Master Gateway Node',
        roles: ['master'],
        supportedModelKinds: ['needle2', 'llm'],
        installedModelIds: ['needle-2-q4km', 'gemma-2-2b-it'],
        capacity: {
          totalCpuCores: 8,
          availableCpuCores: 6,
          totalMemoryMb: 16384,
          availableMemoryMb: 12000,
          maxNeedleInstances: 4,
          activeNeedleInstances: 0,
          maxLlmInstances: 1,
          activeLlmInstances: 0,
        },
        policy: {
          disabled: false,
          quarantined: false,
          draining: false,
          allowMasterFallback: true,
          maxNeedleInstances: 4,
          maxActiveLlmRuntimes: 1,
          allowMixedLlmModels: false,
          recoveryRampUpPhases: [{ durationMs: 'infinite', capacityPercent: 100 }],
        },
        lastHeartbeatAt: new Date().toISOString(),
        lifecycleState: 'ONLINE',
      });
    }

    const [n, r, m] = await Promise.all([
      nodeRegistry.getAllNodes(),
      store.getRuntimes(),
      modelRegistry.getAllModels(),
    ]);

    setNodes(n);
    setRuntimes(r);
    setModels(m);
    if (selectedNode) {
      const updatedSelected = n.find((item) => item.nodeId === selectedNode.nodeId);
      if (updatedSelected) setSelectedNode(updatedSelected);
    }
  };

  useEffect(() => {
    refreshState();
    const interval = setInterval(refreshState, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleScaleNeedle = async (nodeId: string, delta: number) => {
    const nodeWorkers = runtimes.filter((r) => r.nodeId === nodeId && r.kind === 'needle2' && r.status === 'active');
    const node = nodes.find((n) => n.nodeId === nodeId);
    if (!node) return;

    const target = Math.max(0, Math.min(node.policy.maxNeedleInstances, nodeWorkers.length + delta));
    await needleService.scalePool(nodeId, 'needle-2-q4km', target);
    onNotice?.(`⚡ Needle 2 worker pool on ${nodeId} scaled to ${target} instances`);
    await refreshState();
  };

  const handleToggleLlm = async (nodeId: string, modelId: string, shouldStart: boolean) => {
    if (shouldStart) {
      await llmService.startRuntime(nodeId, modelId);
      onNotice?.(`⚡ Started LLM runtime ${modelId} on ${nodeId}`);
    } else {
      const active = runtimes.find((r) => r.nodeId === nodeId && r.modelId === modelId && r.status === 'active');
      if (active) {
        await llmService.stopRuntime(active.runtimeId, 'terminate');
        onNotice?.(`⏸ Stopped LLM runtime on ${nodeId}`);
      }
    }
    await refreshState();
  };

  const handleSimulate = async () => {
    const decision = await router.simulateRouting(simScope as any, simScopeId, simKind);
    setSimResult(decision);
  };

  const handleConfirmForceKill = async () => {
    if (forceKillTarget) {
      await needleService.forceKillWorker(forceKillTarget.runtimeId);
      onNotice?.(`🛑 Force killed runtime ${forceKillTarget.runtimeId}`);
      setForceKillTarget(null);
      await refreshState();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', color: '#f8fafc', fontFamily: 'system-ui, sans-serif' }}>
      {/* Top Header & Sub-Tabs */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(15, 23, 42, 0.6)', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 800, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ZapIcon size={18} color="#38bdf8" />
            <span>Multi-Model Node Orchestration Engine</span>
          </div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
            Independent Needle 2 Worker Pools • Discrete LLM Runtimes • Capacity-Aware Failover
          </div>
        </div>

        <div style={{ display: 'flex', gap: '6px', background: 'rgba(0, 0, 0, 0.3)', padding: '4px', borderRadius: '8px' }}>
          <button
            onClick={() => setActiveTab('fleet')}
            style={{
              background: activeTab === 'fleet' ? '#38bdf8' : 'transparent',
              color: activeTab === 'fleet' ? '#090d16' : '#94a3b8',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            🌐 Node Fleet ({nodes.length})
          </button>
          <button
            onClick={() => setActiveTab('models')}
            style={{
              background: activeTab === 'models' ? '#38bdf8' : 'transparent',
              color: activeTab === 'models' ? '#090d16' : '#94a3b8',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            📦 Model Catalog ({models.length})
          </button>
          <button
            onClick={() => {
              setChannels(channelRegistry.listAllChannels());
              setActiveTab('channels');
            }}
            style={{
              background: activeTab === 'channels' ? '#38bdf8' : 'transparent',
              color: activeTab === 'channels' ? '#090d16' : '#94a3b8',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            👥 Agent Channels ({channels.length || 6})
          </button>
          <button
            onClick={() => {
              setCollections(ragRegistry.listCollections());
              setActiveTab('knowledge');
            }}
            style={{
              background: activeTab === 'knowledge' ? '#38bdf8' : 'transparent',
              color: activeTab === 'knowledge' ? '#090d16' : '#94a3b8',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            📚 Scoped Knowledge ({collections.length || 10})
          </button>
          <button
            onClick={() => setActiveTab('learning')}
            style={{
              background: activeTab === 'learning' ? '#38bdf8' : 'transparent',
              color: activeTab === 'learning' ? '#090d16' : '#94a3b8',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            🛡️ Learning & Quality
          </button>
          <button
            onClick={() => setActiveTab('simulation')}
            style={{
              background: activeTab === 'simulation' ? '#38bdf8' : 'transparent',
              color: activeTab === 'simulation' ? '#090d16' : '#94a3b8',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            🎯 Route Simulation
          </button>
        </div>
      </div>

      {/* View A: Node Fleet Table */}
      {activeTab === 'fleet' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ overflowX: 'auto', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: 'rgba(0, 0, 0, 0.4)', color: '#94a3b8', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <th style={{ padding: '10px 14px' }}>Node</th>
                  <th style={{ padding: '10px 14px' }}>Lifecycle</th>
                  <th style={{ padding: '10px 14px' }}>Memory & CPU</th>
                  <th style={{ padding: '10px 14px' }}>Needle 2 Worker Pool</th>
                  <th style={{ padding: '10px 14px' }}>LLM Runtime</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {nodes.map((node) => {
                  const nodeNeedleWorkers = runtimes.filter((r) => r.nodeId === node.nodeId && r.kind === 'needle2' && r.status === 'active');
                  const nodeLlm = runtimes.find((r) => r.nodeId === node.nodeId && r.kind === 'llm' && r.status === 'active');
                  const isAtCapacity = nodeNeedleWorkers.length >= node.policy.maxNeedleInstances;

                  return (
                    <tr key={node.nodeId} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)', background: node.lifecycleState === 'OFFLINE' ? 'rgba(239, 68, 68, 0.04)' : 'transparent' }}>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontWeight: 700, color: '#f8fafc' }}>{node.nodeName}</div>
                        <div style={{ fontSize: '10px', color: '#64748b', fontFamily: 'monospace' }}>{node.nodeId} • {node.roles.join(', ')}</div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            padding: '3px 8px',
                            borderRadius: '12px',
                            backgroundColor:
                              node.lifecycleState === 'ONLINE'
                                ? 'rgba(16, 185, 129, 0.15)'
                                : node.lifecycleState === 'DEGRADED'
                                ? 'rgba(245, 158, 11, 0.15)'
                                : 'rgba(239, 68, 68, 0.15)',
                            color:
                              node.lifecycleState === 'ONLINE'
                                ? '#34d399'
                                : node.lifecycleState === 'DEGRADED'
                                ? '#fbbf24'
                                : '#f87171',
                            border: `1px solid ${
                              node.lifecycleState === 'ONLINE'
                                ? 'rgba(16, 185, 129, 0.3)'
                                : 'rgba(239, 68, 68, 0.3)'
                            }`,
                          }}
                        >
                          ● {node.lifecycleState}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontSize: '11px', color: '#cbd5e1' }}>
                          {node.capacity.availableMemoryMb} MB / {node.capacity.totalMemoryMb} MB RAM
                        </div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>{node.capacity.availableCpuCores} cores available</div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8' }}>
                            {nodeNeedleWorkers.length} / {node.policy.maxNeedleInstances} Workers
                          </span>
                          <button
                            onClick={() => handleScaleNeedle(node.nodeId, -1)}
                            disabled={nodeNeedleWorkers.length === 0}
                            style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              border: '1px solid rgba(255, 255, 255, 0.15)',
                              background: 'rgba(255, 255, 255, 0.05)',
                              color: '#fff',
                              fontSize: '11px',
                              cursor: nodeNeedleWorkers.length === 0 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            −
                          </button>
                          <button
                            onClick={() => handleScaleNeedle(node.nodeId, 1)}
                            disabled={isAtCapacity}
                            title={isAtCapacity ? `Requires 1,200 MB; node at max capacity (${node.policy.maxNeedleInstances})` : 'Scale up worker'}
                            style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              border: '1px solid rgba(56, 189, 248, 0.4)',
                              background: isAtCapacity ? 'rgba(255, 255, 255, 0.05)' : 'rgba(56, 189, 248, 0.15)',
                              color: isAtCapacity ? '#64748b' : '#38bdf8',
                              fontSize: '11px',
                              cursor: isAtCapacity ? 'not-allowed' : 'pointer',
                            }}
                          >
                            ＋
                          </button>
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        {nodeLlm ? (
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#34d399' }}>
                            ⚡ {nodeLlm.modelId} (Active)
                          </span>
                        ) : (
                          <span style={{ fontSize: '11px', color: '#64748b' }}>○ Inactive</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                        <button
                          onClick={() => setSelectedNode(node)}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            background: 'rgba(255, 255, 255, 0.08)',
                            color: '#cbd5e1',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            fontSize: '11px',
                            cursor: 'pointer',
                          }}
                        >
                          ⚙ Configure
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Node Configuration Drawer */}
          {selectedNode && (
            <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px' }}>
                <div style={{ fontSize: '14px', fontWeight: 800, color: '#38bdf8' }}>
                  Node Configuration: {selectedNode.nodeName} ({selectedNode.nodeId})
                </div>
                <button
                  onClick={() => setSelectedNode(null)}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '16px', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>

              {/* Drawer Grid: Needle2 Panel vs LLM Panel */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {/* Needle 2 Elastic Panel */}
                <div style={{ background: 'rgba(0, 0, 0, 0.3)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#60a5fa', marginBottom: '8px' }}>
                    Needle 2 Worker Pool Settings
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '12px' }}>
                    RAM Headroom allows up to {selectedNode.policy.maxNeedleInstances} parallel schema workers (1,200 MB per worker).
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {runtimes
                      .filter((r) => r.nodeId === selectedNode.nodeId && r.kind === 'needle2')
                      .map((w) => (
                        <div
                          key={w.runtimeId}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: 'rgba(255, 255, 255, 0.03)',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            border: '1px solid rgba(255, 255, 255, 0.06)',
                          }}
                        >
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#f8fafc' }}>
                              Worker #{w.instanceIndex} (g{w.generation})
                            </div>
                            <div style={{ fontSize: '10px', color: '#64748b' }}>
                              Status: {w.status} • {w.activeRequests} active reqs
                            </div>
                          </div>
                          <button
                            onClick={() => setForceKillTarget(w)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.15)',
                              color: '#f87171',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              borderRadius: '4px',
                              padding: '3px 8px',
                              fontSize: '10px',
                              cursor: 'pointer',
                            }}
                          >
                            Stop
                          </button>
                        </div>
                      ))}
                  </div>
                </div>

                {/* LLM Discrete Panel */}
                <div style={{ background: 'rgba(0, 0, 0, 0.3)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#a78bfa', marginBottom: '8px' }}>
                    Discrete LLM Runtimes
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '12px' }}>
                    Max {selectedNode.policy.maxActiveLlmRuntimes} LLM active per node.
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {models
                      .filter((m) => m.kind === 'llm')
                      .map((m) => {
                        const isRunning = runtimes.some(
                          (r) => r.nodeId === selectedNode.nodeId && r.modelId === m.id && r.status === 'active'
                        );
                        return (
                          <div
                            key={m.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              background: isRunning ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255, 255, 255, 0.03)',
                              padding: '8px 10px',
                              borderRadius: '6px',
                              border: isRunning ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(255, 255, 255, 0.06)',
                            }}
                          >
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: 700, color: isRunning ? '#34d399' : '#f8fafc' }}>
                                {m.name}
                              </div>
                              <div style={{ fontSize: '10px', color: '#64748b' }}>{m.estimatedRamMb} MB • GGUF</div>
                            </div>
                            <button
                              onClick={() => handleToggleLlm(selectedNode.nodeId, m.id, !isRunning)}
                              style={{
                                background: isRunning ? 'rgba(239, 68, 68, 0.15)' : 'rgba(56, 189, 248, 0.2)',
                                color: isRunning ? '#f87171' : '#38bdf8',
                                border: isRunning ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(56, 189, 248, 0.4)',
                                borderRadius: '4px',
                                padding: '4px 10px',
                                fontSize: '10px',
                                fontWeight: 700,
                                cursor: 'pointer',
                              }}
                            >
                              {isRunning ? 'Stop' : 'Start'}
                            </button>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* View B: Model Catalog */}
      {activeTab === 'models' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
          {models.map((m) => (
            <div key={m.id} style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '10px', padding: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#f8fafc' }}>{m.name}</div>
                  <div style={{ fontSize: '10px', color: '#38bdf8', marginTop: '2px', textTransform: 'uppercase' }}>
                    {m.kind} • {m.supportsMultiInstance ? 'Multi-Worker Pool' : 'Single Instance'}
                  </div>
                </div>
                <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8' }}>
                  {m.metadataTrust}
                </span>
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>
                RAM: {m.estimatedRamMb} MB • Capabilities: {m.capabilities.join(', ')}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View C: Agent Channels Administration */}
      {activeTab === 'channels' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ overflowX: 'auto', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: 'rgba(0, 0, 0, 0.4)', color: '#94a3b8', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <th style={{ padding: '10px 14px' }}>Channel</th>
                  <th style={{ padding: '10px 14px' }}>Models (LLM / Needle)</th>
                  <th style={{ padding: '10px 14px' }}>RAG Knowledge Bounds</th>
                  <th style={{ padding: '10px 14px' }}>Access (RBAC / Groups)</th>
                  <th style={{ padding: '10px 14px' }}>Status</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right' }}>Toggle</th>
                </tr>
              </thead>
              <tbody>
                {(channels.length > 0 ? channels : channelRegistry.listAllChannels()).map((ch) => (
                  <tr key={ch.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 700, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>{ch.icon}</span> <span>{ch.name}</span>
                      </div>
                      <div style={{ fontSize: '10px', color: '#64748b' }}>ID: <code>{ch.id}</code></div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: '11px', color: '#cbd5e1' }}>LLM: <code>{ch.llmModelId}</code></div>
                      {ch.needleModelId && <div style={{ fontSize: '10px', color: '#a78bfa' }}>Needle: <code>{ch.needleModelId}</code></div>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: '10px', color: '#38bdf8' }}>
                        {ch.ragCollectionIds.length} collections ({ch.ragCollectionIds.join(', ')})
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: '10px', color: '#34d399' }}>
                        {ch.accessPolicy.publicAccess === 'authenticated' ? 'Authenticated Public' : `Roles: [${ch.accessPolicy.allowedRoles.join(', ')}]`}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', background: ch.enabled ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)', color: ch.enabled ? '#34d399' : '#f87171' }}>
                        {ch.enabled ? 'ENABLED' : 'DISABLED'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                      <button
                        onClick={() => {
                          const updated = channelRegistry.setChannelStatus(ch.id, !ch.enabled);
                          setChannels(channelRegistry.listAllChannels());
                          if (onNotice) onNotice(`Channel ${ch.name} ${updated.enabled ? 'Enabled' : 'Disabled'}`);
                        }}
                        style={{ padding: '4px 10px', background: ch.enabled ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '6px', color: ch.enabled ? '#f87171' : '#34d399', fontSize: '10px', fontWeight: 600, cursor: 'pointer' }}
                      >
                        {ch.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View D: Scoped Knowledge (RAG & Wiki Collections) */}
      {activeTab === 'knowledge' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
          {(collections.length > 0 ? collections : ragRegistry.listCollections()).map((col) => (
            <div key={col.id} style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '10px', padding: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#f8fafc' }}>{col.name}</div>
                  <div style={{ fontSize: '10px', color: '#a78bfa', marginTop: '2px' }}>URI: <code>{col.id}</code></div>
                </div>
                <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', background: col.scopeType === 'plugin' ? 'rgba(56, 189, 248, 0.15)' : 'rgba(168, 85, 247, 0.15)', color: col.scopeType === 'plugin' ? '#38bdf8' : '#c084fc' }}>
                  {col.scopeType.toUpperCase()}
                </span>
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>
                {col.description}
              </div>
              <div style={{ fontSize: '10px', color: '#64748b', marginTop: '8px' }}>
                Documents: {col.documentCount} • Chunks: {col.chunkCount}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View E: Governed Learning & Quality Dashboard */}
      {activeTab === 'learning' && (
        <AgentQualityLearningDashboard onNotice={onNotice} />
      )}

      {/* View C: Route Simulation */}
      {activeTab === 'simulation' && (
        <div style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#38bdf8' }}>Dry-Run Routing Policy Inspector</div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              value={simKind}
              onChange={(e) => setSimKind(e.target.value as any)}
              style={{ background: '#0f172a', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.2)', padding: '6px 10px', borderRadius: '6px', fontSize: '11px' }}
            >
              <option value="needle2">Needle 2 Engine</option>
              <option value="llm">General LLM</option>
            </select>
            <input
              type="text"
              value={simScopeId}
              onChange={(e) => setSimScopeId(e.target.value)}
              placeholder="Scope ID (e.g. shopping-cart-assistant)"
              style={{ background: '#0f172a', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.2)', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', flex: 1 }}
            />
            <button
              onClick={handleSimulate}
              style={{ background: '#38bdf8', color: '#090d16', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
            >
              🎯 Simulate Dispatch
            </button>
          </div>

          {simResult && (
            <div style={{ background: 'rgba(0, 0, 0, 0.3)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(56, 189, 248, 0.2)', fontSize: '11px', fontFamily: 'monospace' }}>
              <div style={{ color: '#34d399', fontWeight: 700 }}>
                Selected Target: {simResult.selectedNodeId || 'NONE'} • Runtime: {simResult.selectedRuntimeId || 'NONE'}
              </div>
              <div style={{ color: '#94a3b8', marginTop: '4px' }}>Score: {simResult.score}</div>
              <div style={{ color: '#cbd5e1', marginTop: '6px' }}>Reasons: {simResult.reasons.join(' | ')}</div>
            </div>
          )}
        </div>
      )}

      {/* Force Kill Confirmation Modal */}
      {forceKillTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#1e293b', border: '1px solid #ef4444', borderRadius: '12px', padding: '20px', maxWidth: '400px', width: '90%' }}>
            <div style={{ fontSize: '14px', fontWeight: 800, color: '#f87171' }}>⚠️ Confirm Force Kill</div>
            <div style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '8px' }}>
              Are you sure you want to force terminate runtime <code>{forceKillTarget.runtimeId}</code>? In-flight requests may be orphaned.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button
                onClick={() => setForceKillTarget(null)}
                style={{ padding: '6px 12px', borderRadius: '6px', background: '#334155', color: '#fff', border: 'none', fontSize: '11px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmForceKill}
                style={{ padding: '6px 12px', borderRadius: '6px', background: '#ef4444', color: '#fff', border: 'none', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
              >
                Force Kill
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
